import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';

import Transmitigator, { TransmitRef } from '@ally/transmitigator';
import { useConfig } from '../../hooks';
import TransmitToken from '../../models/TransmitToken';

export interface ITransmitContext {
  transmitRef: TransmitRef;
  transmitToken: TransmitToken;
  setTransmitToken: (token: TransmitToken) => void;
}

const TransmitContext = createContext<ITransmitContext>({} as ITransmitContext);

export const useTransmitRef = (): ITransmitContext =>
  useContext(TransmitContext);

export const TransmitProvider: FC<PropsWithChildren> = ({ children }) => {
  const { transmitConfig } = useConfig();
  const [transmitToken, setTransmitToken] = useState(new TransmitToken());

  const transmitigatorInstance = useMemo(() => {
    // @ally/transmitigator@29 ships as CJS (exports.default = factory).
    // Vite's CJS→ESM interop may surface the namespace object; unwrap defensively.
    const factory =
      typeof Transmitigator === 'function'
        ? Transmitigator
        : (Transmitigator as any).default;
    return factory({
      server: transmitConfig.baseUrl,
      application: {
        id: 'ALLYUSBOLB',
        name: 'AOB',
        version: '1.0',
        apigeeApiKey: transmitConfig.apiKey,
      },
      // Disable AllyTransportProvider (uses fetch() without credentials:'include')
      // so the SDK falls back to its default XmSdk XMLHttpRequest transport with
      // withCredentials=true. This ensures the ally-ciam-token cookie (set by the
      // bank during SSO navigation) is sent cross-origin to the Transmit server.
      disableAllyTransportProvider: true,
    });
  }, []);

  const hookInstance = transmitigatorInstance.useTransmit();

  const transmitRef = {
    ...transmitigatorInstance,
    useTransmit: () => hookInstance,
  };

  return (
    <TransmitContext.Provider
      value={{
        transmitRef,
        transmitToken,
        setTransmitToken,
      }}
    >
      {children}
    </TransmitContext.Provider>
  );
};
